/**
 * PAPPO — Input Component
 * Campo de texto reutilizável com ícone opcional, label e mensagem de erro.
 * Focus ring indigo para acessibilidade.
 */

import React, { forwardRef, useId } from 'react';
import styles from './Input.module.css';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label do campo */
  label?: string;
  /** Mensagem de erro */
  error?: string;
  /** Ícone à esquerda (Lucide element) */
  leftIcon?: React.ReactNode;
  /** Texto auxiliar */
  helperText?: string;
  /** Tamanho */
  inputSize?: 'sm' | 'md' | 'lg';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, leftIcon, helperText, inputSize = 'md', className = '', id: externalId, ...rest }, ref) => {
    const autoId = useId();
    const inputId = externalId || autoId;
    const errorId = error ? `${inputId}-error` : undefined;
    const helperId = helperText ? `${inputId}-helper` : undefined;

    const wrapperClasses = [
      styles.inputWrapper,
      styles[inputSize],
      error ? styles.hasError : '',
      leftIcon ? styles.hasIcon : '',
      className,
    ].filter(Boolean).join(' ');

    return (
      <div className={styles.field}>
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
          </label>
        )}

        <div className={wrapperClasses}>
          {leftIcon && (
            <span className={styles.icon} aria-hidden="true">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={styles.input}
            aria-invalid={!!error}
            aria-describedby={[errorId, helperId].filter(Boolean).join(' ') || undefined}
            {...rest}
          />
        </div>

        {error && (
          <p id={errorId} className={styles.error} role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className={styles.helper}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
